import { isAnyOf } from "@reduxjs/toolkit";
import { RootState } from "@store/index";
import { GetAppListener } from "@shared/models/redux";
import { effectiveUserSelector } from "@user/store/selectors";
import { fetchUserProfile } from "@user/store/thunks/fetchUserProfile";
import { startImpersonationSession, stopImpersonationSession } from "@impersonateUser/store/actions";
import { datadogRum } from "./datadogRum";
import { isDatadogRumEnabled } from "./config";

// Non-PII user context attached to the RUM session. Deliberately excludes
// `email` and `name`. The index signature makes it assignable to the Datadog
// SDK `User` type (an extensible bag) without widening intent.
export interface RumUserContext {
  id: string;
  userType: string;
  external: boolean;
  impersonated: boolean;
  [key: string]: unknown;
}

/**
 * Pure mapping from store state to the RUM user context. Uses the effective
 * (impersonated-or-logged-in) identity so journeys are attributed to the active
 * user, consistent with RequestService's `entitledUserId` behavior. Returns
 * `null` when there is no user to attribute.
 */
export const toRumUser = (state: RootState): RumUserContext | null => {
  const { data } = effectiveUserSelector(state);

  if (!data) return null;

  const impersonated = Boolean(
    (state as { impersonation?: { isImpersonating?: boolean } }).impersonation?.isImpersonating
  );

  return {
    id: data.id,
    userType: data.userType,
    external: data.external,
    impersonated,
  };
};

/**
 * Listener that keeps the RUM user in sync with login and impersonation
 * changes. Registered alongside the app's other listeners in
 * `useStartAppListeners`.
 */
export const getDatadogRumUserListener: GetAppListener = (addAppListener) =>
  addAppListener({
    matcher: isAnyOf(fetchUserProfile.fulfilled, startImpersonationSession, stopImpersonationSession),
    effect: (_action, { getState }) => {
      if (!isDatadogRumEnabled()) return;

      const rumUser = toRumUser(getState());

      if (rumUser) {
        datadogRum.setUser(rumUser);
      } else {
        datadogRum.clearUser();
      }
    },
  });
